import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { useEffect, useMemo, useRef } from 'react';
import { type Group, MathUtils, type PerspectiveCamera, Vector3 } from 'three';
import { useNavigationStore } from '@/store/useNavigationStore';
import { getPage } from '@/objects/pages';

/**
 * Camera + world choreographer for the kavşak.
 *
 * Warp timeline (≈3.3s total):
 *   1. LIFT       (0.00 → 0.30 of SPIN)  camera rises straight up to a
 *                                        bird's-eye view of the junction
 *   2. ORBIT      (0.30 → 0.70 of SPIN)  hold bird's-eye while the world
 *                                        does a full revolution + aligns
 *                                        the selected highway with -Z
 *   3. DESCEND    (0.70 → 1.00 of SPIN)  camera drops back down to road
 *                                        level on the entry side
 *   4. DIVE       (DIVE phase)           accelerated forward fly-through,
 *                                        slow → very fast (ease-in-quart),
 *                                        stops in front of the page screen
 *
 * Returning is a single eased reverse to the idle pose.
 */

const SPIN_MS = 5500;
const DIVE_MS = 3200;
const RETURN_MS = 1500;
/** Number of full revolutions the kavşak performs during the spin. */
const SPIN_REVOLUTIONS = 6;

// VR-specific timings: cinematic but shorter than desktop warp because
// in VR every second of forced motion is a motion-sickness risk.
const VR_TRAVEL_MS = 2400;
const VR_RETURN_MS = 1500;
const VR_TRAVEL_DISTANCE = 85;
/** Asymmetric thumbstick speeds: forward axis is brisk so the user
 *  can cover ground down a long highway without holding the stick
 *  for minutes; strafe stays calmer so it doesn't drift them off the
 *  road centerline as easily. */
const VR_FORWARD_SPEED = 5.0;
const VR_STRAFE_SPEED = 2.5;
const VR_SNAP_ROTATION = (Math.PI / 180) * 30;
const VR_DEADZONE = 0.18;
/** Free-roam radius around the kavşak when phase === 'idle'. */
const VR_HUB_RADIUS = 6.0;
/** Half-width of the perpendicular wobble allowed when walking down
 *  a highway in 'arrived' (matches the visual road width 2.4 / 2). */
const VR_ROAD_HALF_WIDTH = 1.2;

// Idle IS the full bird's-eye — camera straight above the junction
// looking down. Same pose the warp lifts to mid-spin. Signposts are
// billboarded so they remain readable from this overhead angle.
const IDLE_CAM = new Vector3(0, 14, 0.4);
const IDLE_TARGET = new Vector3(0, 0, 0);
const BIRDS_EYE_CAM = IDLE_CAM;
const BIRDS_EYE_TARGET = IDLE_TARGET;
// After the bird's-eye spin we descend INTO the central hub disc and
// face along the highway, so the dive starts from the white junction
// itself — the signpost stays in peripheral vision and never blocks
// the centre of the screen.
const SPIN_END_CAM = new Vector3(0, 2.0, 0.8);
const SPIN_END_TARGET = new Vector3(0, 1.7, -8);
const ARRIVED_TARGET = new Vector3(0, 1.7, -100);

const SPIN_PHASE_LIFT = 0.3;
const SPIN_PHASE_DESCEND = 0.7;

function easeInOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}
function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}
/** Smooth S-curve: accel through first half, sustained peak speed
 *  near the middle, decel through second half. Good for a dive that
 *  needs to FEEL like a long light-speed travel rather than a short
 *  whip. */
function easeInOutSine(x: number): number {
  return -(Math.cos(Math.PI * x) - 1) / 2;
}
/** Spin curve for the kavşak rotation:
 *  - Starts with immediate visible motion (linear-ish push from t=0)
 *  - Builds to a strong peak around the middle (visible çark)
 *  - Decelerates smoothly to a complete stop at t=1
 *  Combination of easeOutQuad (gives a non-zero starting velocity) and
 *  easeInOutQuart (provides the strong middle peak and the zero-end). */
function spinEase(x: number): number {
  const eOutQuad = 1 - (1 - x) * (1 - x);
  const eInOutQuart = x < 0.5
    ? 8 * x * x * x * x
    : 1 - Math.pow(-2 * x + 2, 4) / 2;
  return 0.15 * eOutQuad + 0.85 * eInOutQuart;
}

interface NavigatorProps {
  worldRef: React.RefObject<Group | null>;
  xrOriginRef: React.RefObject<Group | null>;
}

export function Navigator({ worldRef, xrOriginRef }: NavigatorProps) {
  const camera = useThree((s) => s.camera);
  const xrSession = useXR((s) => s.session);
  const isVR = !!xrSession;

  const phase = useNavigationStore((s) => s.phase);
  const transitionStartedAt = useNavigationStore((s) => s.transitionStartedAt);
  const pageId = useNavigationStore((s) => s.page);
  const setPhase = useNavigationStore((s) => s.setPhase);


  const fromYawRef = useRef(0);
  const fromPosRef = useMemo(() => new Vector3(), []);
  const fromTargetRef = useMemo(() => new Vector3(), []);
  const tmpPos = useMemo(() => new Vector3(), []);
  const tmpTarget = useMemo(() => new Vector3(), []);
  /** Mutable arrival pose — Z bumped closer on portrait viewports so
   *  the destination PageScreen (10.5 wide) doesn't overflow when
   *  the camera FOV is widened on phones. Updated every frame. */
  const arrivedCamMutable = useMemo(() => new Vector3(0, 1.7, -85), []);
  // VR scratch state (XROrigin start pose for travel / return lerps,
  // snap-rotation latch, and the helper vectors used in locomotion).
  const vrFromPosRef = useMemo(() => new Vector3(), []);
  const vrSnapLatchRef = useRef(false);
  const vrTmpForward = useMemo(() => new Vector3(), []);
  const vrTmpRight = useMemo(() => new Vector3(), []);
  const vrTmpMove = useMemo(() => new Vector3(), []);

  // Drop the desktop short-circuit in favour of a real VR animation
  // path below. We still need to capture the XROrigin start pose when
  // a transition kicks off so the lerp has a clean starting point.
  useEffect(() => {
    if (!isVR) return;
    if (phase === 'warping' || phase === 'returning') {
      vrFromPosRef.copy(xrOriginRef.current?.position ?? new Vector3());
    }
  }, [isVR, phase, transitionStartedAt, xrOriginRef, vrFromPosRef]);

  // Snapshot start values when a transition kicks off
  useEffect(() => {
    if (phase === 'warping' || phase === 'returning') {
      fromYawRef.current = worldRef.current?.rotation.y ?? 0;
      fromPosRef.copy(camera.position);
      const fwd = new Vector3();
      camera.getWorldDirection(fwd);
      fromTargetRef.copy(camera.position).addScaledVector(fwd, 8);
    }
  }, [phase, transitionStartedAt, camera, worldRef, fromPosRef, fromTargetRef]);

  // Initial pose
  useEffect(() => {
    camera.position.copy(IDLE_CAM);
    camera.lookAt(IDLE_TARGET);
  }, [camera]);

  useFrame((state, frameDt) => {
    const world = worldRef.current;
    const page = getPage(pageId);
    const targetYaw = page ? -page.yaw : 0;

    // Aspect-aware FOV: on portrait viewports the horizontal slice of
    // the perspective camera shrinks below the kavşak's width and the
    // outer signposts get clipped. We bump vertical FOV so that the
    // horizontal coverage stays roughly the same on phones.
    const aspect = state.size.width / state.size.height;
    const cam = state.camera as PerspectiveCamera;
    if (typeof cam.fov === 'number') {
      const targetFov = aspect < 1 ? Math.min(80, 45 / aspect) : 45;
      if (Math.abs(cam.fov - targetFov) > 0.5) {
        cam.fov = targetFov;
        cam.updateProjectionMatrix();
      }
    }

    // Aspect-aware arrival pose — pull the camera back further on
    // portrait so the PageScreen (~10.5 wide) fits within the wider
    // FOV's still-narrower horizontal slice.
    arrivedCamMutable.set(0, 1.7, aspect < 1 ? -78 : -85);

    // ── VR / XR session: smooth travel down the active highway, with
    //    free joystick locomotion + snap-rotation once arrived. The
    //    kavşak (worldRef) does NOT spin in VR — only the user moves.
    if (isVR) {
      const xr = xrOriginRef.current;
      if (world) world.rotation.y = 0;
      if (!xr) return;

      // Direction in WORLD space toward the active page's screen.
      // (Highways are static in VR — each page lives at its own yaw.)
      const yawSel = page ? page.yaw : 0;
      const dirX = -Math.sin(yawSel);
      const dirZ = -Math.cos(yawSel);
      const targetX = dirX * VR_TRAVEL_DISTANCE;
      const targetZ = dirZ * VR_TRAVEL_DISTANCE;

      if (phase === 'idle') {
        // Allow free roaming inside the hub area — user wanted to be
        // able to walk near the kavşak even before clicking a sign.
        applyVRLocomotion(state, xr, frameDt);
        constrainToHub(xr);
      } else if (phase === 'warping') {
        // Smooth forward travel — light-speed feel without the camera
        // pitch/yaw of the desktop dive.
        const elapsed = performance.now() - transitionStartedAt;
        const tt = Math.min(1, elapsed / VR_TRAVEL_MS);
        const e = easeInOutSine(tt);
        xr.position.x = MathUtils.lerp(vrFromPosRef.x, targetX, e);
        xr.position.z = MathUtils.lerp(vrFromPosRef.z, targetZ, e);
        if (tt >= 1) setPhase('arrived');
      } else if (phase === 'arrived') {
        // Free roam with the left joystick, then snap the result back
        // onto the highway centerline so the player can't walk off
        // into space.
        applyVRLocomotion(state, xr, frameDt);
        if (page) constrainToHighway(xr, page.yaw);
      } else if (phase === 'returning') {
        const elapsed = performance.now() - transitionStartedAt;
        const tt = Math.min(1, elapsed / VR_RETURN_MS);
        const e = easeInOutSine(tt);
        xr.position.x = MathUtils.lerp(vrFromPosRef.x, 0, e);
        xr.position.z = MathUtils.lerp(vrFromPosRef.z, 0, e);
        if (tt >= 1) setPhase('idle');
      }

      // Snap-rotation + B-button shortcut work regardless of phase.
      applyVRController(xr);
      return;
    }

    // Helper: free locomotion driven by the LEFT thumbstick. Movement
    // direction is the camera's forward/right vectors projected onto
    // the XZ plane; speed is constant. Player can walk anywhere.
    function applyVRLocomotion(rootState: typeof state, xr: Group, dt: number) {
      if (!xrSession) return;
      let stickX = 0;
      let stickY = 0;
      for (const src of xrSession.inputSources) {
        if (src.handedness === 'left' && src.gamepad) {
          stickX = src.gamepad.axes[2] ?? 0;
          stickY = src.gamepad.axes[3] ?? 0;
          break;
        }
      }
      if (Math.abs(stickX) < VR_DEADZONE && Math.abs(stickY) < VR_DEADZONE) return;
      rootState.camera.getWorldDirection(vrTmpForward);
      vrTmpForward.y = 0;
      vrTmpForward.normalize();
      // Right vector = forward × up. For forward (0,0,-1): right=(1,0,0).
      vrTmpRight.set(-vrTmpForward.z, 0, vrTmpForward.x);
      const stepDt = Math.min(0.05, dt || 1 / 60);
      vrTmpMove.set(0, 0, 0);
      vrTmpMove.addScaledVector(vrTmpForward, -stickY * VR_FORWARD_SPEED * stepDt);
      vrTmpMove.addScaledVector(vrTmpRight, stickX * VR_STRAFE_SPEED * stepDt);
      xr.position.add(vrTmpMove);
    }

    // Clamp the XROrigin to a corridor around the active highway so
    // the player can walk forward / backward along the road and wobble
    // up to VR_ROAD_HALF_WIDTH perpendicular, but can't drift into
    // empty space. Range [2, 110] keeps them between the hub edge and
    // just past the page screen.
    function constrainToHighway(xr: Group, yaw: number) {
      const dirX = -Math.sin(yaw);
      const dirZ = -Math.cos(yaw);
      const along = xr.position.x * dirX + xr.position.z * dirZ;
      const clampedAlong = Math.max(2, Math.min(110, along));
      const perpX = xr.position.x - dirX * along;
      const perpZ = xr.position.z - dirZ * along;
      const perp = Math.hypot(perpX, perpZ);
      if (perp > VR_ROAD_HALF_WIDTH) {
        const scale = VR_ROAD_HALF_WIDTH / perp;
        xr.position.x = dirX * clampedAlong + perpX * scale;
        xr.position.z = dirZ * clampedAlong + perpZ * scale;
      } else {
        xr.position.x = dirX * clampedAlong + perpX;
        xr.position.z = dirZ * clampedAlong + perpZ;
      }
    }

    // Constrain the XROrigin to a circle around the central junction
    // when phase is idle so the user can walk freely near the hub but
    // can't wander off into empty space.
    function constrainToHub(xr: Group) {
      const r = Math.hypot(xr.position.x, xr.position.z);
      if (r > VR_HUB_RADIUS) {
        const scale = VR_HUB_RADIUS / r;
        xr.position.x *= scale;
        xr.position.z *= scale;
      }
    }

    // Helper: snap-rotation on the right thumbstick and a B-button
    // (right index 5) shortcut for "Geri Dön".
    function applyVRController(xr: Group) {
      if (!xrSession) return;
      for (const src of xrSession.inputSources) {
        if (src.handedness !== 'right' || !src.gamepad) continue;
        const rotX = src.gamepad.axes[2] ?? 0;
        if (Math.abs(rotX) > 0.7 && !vrSnapLatchRef.current) {
          xr.rotation.y -= Math.sign(rotX) * VR_SNAP_ROTATION;
          vrSnapLatchRef.current = true;
        } else if (Math.abs(rotX) < 0.3) {
          vrSnapLatchRef.current = false;
        }
        const bPressed = !!src.gamepad.buttons[5]?.pressed;
        if (bPressed && (phase === 'arrived' || phase === 'warping')) {
          useNavigationStore.getState().returnHome();
        }
      }
    }

    if (phase === 'idle') {
      if (world) world.rotation.y = MathUtils.lerp(world.rotation.y, 0, 0.08);
      camera.position.lerp(IDLE_CAM, 0.06);
      tmpTarget.copy(IDLE_TARGET);
      camera.lookAt(tmpTarget);
      return;
    }

    if (phase === 'warping') {
      const elapsed = performance.now() - transitionStartedAt;

      // ── Sub-phases 1-3: SPIN (lift → orbit → descend) ───────────────
      if (elapsed < SPIN_MS) {
        const t = elapsed / SPIN_MS;

        // Camera trajectory: piecewise lift / hold / descend
        if (t < SPIN_PHASE_LIFT) {
          const u = t / SPIN_PHASE_LIFT;
          const e = easeOutCubic(u);
          tmpPos.copy(fromPosRef).lerp(BIRDS_EYE_CAM, e);
          tmpTarget.copy(IDLE_TARGET).lerp(BIRDS_EYE_TARGET, e);
        } else if (t < SPIN_PHASE_DESCEND) {
          tmpPos.copy(BIRDS_EYE_CAM);
          tmpTarget.copy(BIRDS_EYE_TARGET);
        } else {
          const u = (t - SPIN_PHASE_DESCEND) / (1 - SPIN_PHASE_DESCEND);
          const e = easeInOutCubic(u);
          tmpPos.copy(BIRDS_EYE_CAM).lerp(SPIN_END_CAM, e);
          tmpTarget.copy(BIRDS_EYE_TARGET).lerp(SPIN_END_TARGET, e);
        }
        camera.position.copy(tmpPos);
        camera.lookAt(tmpTarget);

        // World rotation: starts the instant the user clicks (t=0)
        // with a visible non-zero velocity, accelerates through the
        // bird's-eye hold to a strong peak, then decelerates to a
        // complete stop by t=0.75 — well before the dive begins —
        // so the transition into the dive is smooth and not abrupt.
        if (world) {
          const rotT = Math.min(1, t / 0.75);
          const worldE = spinEase(rotT);
          world.rotation.y = MathUtils.lerp(
            fromYawRef.current,
            SPIN_REVOLUTIONS * 2 * Math.PI + targetYaw,
            worldE,
          );
        }
        return;
      }

      // ── Sub-phase 4: DIVE forward, light-speed travel ─────────────
      // easeInOutSine builds up to a sustained peak speed in the
      // middle of the dive (light-speed feel), then decelerates
      // smoothly into the page screen.
      const t = Math.min(1, (elapsed - SPIN_MS) / DIVE_MS);
      const e = easeInOutSine(t);

      if (world) world.rotation.y = targetYaw;
      tmpPos.copy(SPIN_END_CAM).lerp(arrivedCamMutable, e);
      tmpTarget.copy(SPIN_END_TARGET).lerp(ARRIVED_TARGET, e);
      camera.position.copy(tmpPos);
      camera.lookAt(tmpTarget);

      if (t >= 1) setPhase('arrived');
      return;
    }

    if (phase === 'arrived') {
      if (world) world.rotation.y = targetYaw;
      camera.position.copy(arrivedCamMutable);
      camera.lookAt(ARRIVED_TARGET);
      return;
    }

    if (phase === 'returning') {
      const elapsed = performance.now() - transitionStartedAt;
      const t = Math.min(1, elapsed / RETURN_MS);
      const e = easeInOutCubic(t);
      if (world) {
        world.rotation.y = MathUtils.lerp(fromYawRef.current, 0, e);
      }
      tmpPos.copy(fromPosRef).lerp(IDLE_CAM, e);
      tmpTarget.copy(fromTargetRef).lerp(IDLE_TARGET, e);
      camera.position.copy(tmpPos);
      camera.lookAt(tmpTarget);
      if (t >= 1) setPhase('idle');
      return;
    }
  });

  return null;
}
